import { Component, OnInit, OnDestroy, inject, signal, effect, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIf, NgClass, NgFor, CurrencyPipe, DatePipe } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';

import * as models from '../../interfaces/model';
import { IProfile } from './../../../shared/interfaces/IProfile';

import { OrdersService } from '../orders/orders.service';
import { UserService } from '../../../user/user.service';
import { TopService } from '../../../shared/services/top.service';
import { PubSubService } from '../../../core/pubsub.service';
import { SharedSource } from '../../../core/shared-source';
import { SessionService } from '../../../services/session.service';

import { SharedService } from '../../../shared/shared.service';
import { OrderStatusEnum } from '../../enums/orderStatusEnum';
import { CustomCurrencyPipe } from '../../../shared/pipes/currency.pipe';
import { ErrorModalComponent } from '../../../shared/ui-components/error-modal/error-modal.component';

@Component({
  selector: 'app-full-order',
  templateUrl: './full-order.component.html',
  styleUrls: ['./full-order.component.scss'],
  standalone: true,
  imports: [NgIf, RouterLink, NgClass, NgFor, ErrorModalComponent, CurrencyPipe, DatePipe, CustomCurrencyPipe]
})
export class FullOrderComponent implements OnInit, OnDestroy {
  ordersService = inject(OrdersService);
  topService = inject(TopService);
  userService = inject(UserService);
  router = inject(Router);
  pubSubService = inject(PubSubService);
  route = inject(ActivatedRoute);
  sessionService = inject(SessionService);
  sharedService = inject(SharedService);

  shipping_address = signal<string[]>([]);
  errorModal = signal<models.IBuyerErrorModal>({
    bodyText: '',
    title: 'Server Error',
    isShown: false
  });
  isEditingFromFullOrder = signal(false);
  billing_address = signal<string[]>([]);
  selectedOrder = signal<models.Order | null>(null);

  terms = signal<string>('');
  showCancelModal = signal(false);
  showModal = signal(false);
  cancelError = signal(false);
  animateOut = signal(false);
  animateIn = signal(false);
  order = signal<any>(null);
  isSuper = signal(false);
  showChargeOrder = signal(false);
  showChargedOrder = signal(false);
  SalesOrderStatus = models.SalesOrderStatus;
  docEntrySignal = signal<string | null>(this.route.snapshot.paramMap.get('docEntry'));
  isLoading = signal<boolean>(true);
  hasInitializedChangeCustomer = false;

  constructor() {
    this.ordersService.filterAndMapSalesOrderStatus();

    effect(() => {
      const changeCustomerSignal = this.pubSubService.getSignal(SharedSource.changeCustomer);
      const value = changeCustomerSignal();

      if (!this.hasInitializedChangeCustomer) {
        this.hasInitializedChangeCustomer = true;
        return;
      }

      if (value) {
        this.router.navigate(['user/orders']);
      }
    });
  }

  ngOnInit(): void {
    if (this.topService.isMobile) {
      this.openPdf(null, +this.docEntrySignal());
      this.router.navigate(['user/orders']);
      return;
    }
    this.isSuper.set(this.sessionService.userRole === 'SUPER' ? true : false);

    this.topService.trendingNavVisible = true;
    this.sharedService.isLogoutPage = false;
    this.getData();
  }

  openPdf(event: Event, docEntry: number): void {
    if (this.topService.loading) return;
    this.ordersService.openPdf(event, docEntry);
  }

  getData(): void {
    if (this.ordersService?.selectedOrder?.docNumber) {
      this.selectedOrder.update((prev) => ({
        ...prev!,
        docEntry: this.ordersService.selectedOrder.docEntry,
        docNumber: this.ordersService.selectedOrder.docNumber,
        orderDate: this.ordersService.selectedOrder.orderDate,
        dueDate: this.ordersService.selectedOrder.dueDate,
        orderStatus: this.ordersService.selectedOrder.orderStatus,
        orderTotal: this.ordersService.selectedOrder.orderTotal,
        progressStatus: this.ordersService.selectedOrder.progressStatus,
        beingUpdated: this.ordersService.selectedOrder.beingUpdated,
        payment: this.ordersService.selectedOrder.payment
      }));
    }

    const docEntry = this.docEntrySignal();
    if (docEntry) {
      this.isLoading.set(true);
      this.ordersService.getOrder(docEntry).subscribe({
        next: (order) => {
          this.selectedOrder.set(order);
          this.selectedOrder.set({
            ...order,
            comments: order.comments?.replace(/[\r\n]+|[\r\n]\s+\S*/g, ' | ') || ''
          });

          const billingAddress = this.selectedOrder().billingAddress;
          this.shipping_address.set(billingAddress ? billingAddress.split('\r') : []);

          const shippingAddress = this.selectedOrder().shippingAddress;
          this.billing_address.set(shippingAddress ? shippingAddress.split('\r') : []);

          if (this.isEditingFromFullOrder()) {
            this.userService.getProfile().subscribe(
              (profile: IProfile) => {
                this.userService.discount = profile.discount;
                this.userService.companyName = profile.companyName;
                this.userService.bpCode = profile.bpCode;
                this.userService.orderBankInfo = profile.beneficiaryBank;
              },
              (err) => {
                this.sharedService.handleBuyerHttpError(err, this.errorModal());
              });
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          //console.error('Error getting order:', err);
          this.isLoading.set(false);
          this.router.navigate(['/buyer/home']);
        }
      });
    }
  }

  totalAmount = computed(() => {
    const order = this.selectedOrder();
    return order?.orderDetails?.length
      ? order.orderDetails.reduce((sum, detail) => sum + detail.quantity * detail.unitPrice, 0)
      : 0;
  });

  popUpTextMessages = computed(() => {
    if (this.showModal()) {
      return 'To edit an order, your shopping cart will be cleared.';
    } else if (this.showCancelModal()) {
      return this.cancelError() ? 'You cannot cancel this order' : 'Are you sure you want to CANCEL this order?';
    }
    return '';
  });

  canEditOrder = computed(() => {
    const order = this.selectedOrder();
    const role = this.userService.getRole();
    if (!order) return false;
    return (
      (order.orderStatus === OrderStatusEnum.offerAccepted && role === 'SUPER' && !order.beingUpdated) ||
      (order.orderStatus === OrderStatusEnum.open && (role === 'SALES' || role === 'SUPER') && !order.beingUpdated) ||
      (order.orderStatus === OrderStatusEnum.paid && order.payment.status === 'Authorize' && role === 'SUPER')
    );
  });

  canSplitOrCancelOrder = computed(() => {
    const order = this.selectedOrder();
    const role = this.userService.getRole();
    return role === 'SUPER' && order
      ? [OrderStatusEnum.open, OrderStatusEnum.offerAccepted].includes(order.orderStatus as OrderStatusEnum)
      : false;
  });

  isShowModalOrShowCancelModal = computed(() => this.showModal() || this.showCancelModal());

  updateOrderConfirmDialog(event, order): void {
    this.ordersService.handleFirstOrderLoadRequired = true;
    this.ordersService.selected_doc_number = order.docNumber;
    this.ordersService.selected_order_number = order.docEntry;
    this.sessionService.selectedOrderNumber = this.ordersService.selected_order_number.toString();
    this.sessionService.selectedDocNumber = this.ordersService.selected_doc_number.toString();
    this.openModal();
  }

  openModal(): void {
    this.showModal.set(true);
    this.animateIn.set(true);
  }

  closeModal(): void {
    this.animateIn.set(false);
    this.animateOut.set(true);
    setTimeout(() => {
      this.showModal.set(false);
      this.animateOut.set(false);
    }, 400);
  }


  cancelConfirm(order: models.Order): void {
    this.showCancelModal.set(true);
    this.animateIn.set(true);
    this.animateOut.set(false);
    this.cancelError.set(false);
  }

  cancelOrderYes(): void {
    this.topService.loading = true;
    this.ordersService.cancelOrder(this.selectedOrder().docEntry).subscribe(response => {
      this.topService.searchString = '';
      this.animateIn.set(false);
      this.showCancelModal.set(false);
      this.animateOut.set(true);
      this.topService.loading = false;
      this.router.navigate(['/user/orders']);
    },
      err => {
        this.topService.loading = false;
        this.topService.searchString = '';
        this.cancelError.set(true);
      }
    );
  }

  closeCancelConfirm(): void {
    this.animateIn.set(false);
    this.animateOut.set(true);
    setTimeout(() => {
      this.showCancelModal.set(false);
      this.animateOut.set(false);
    }, 400);
  }

  gotoShopping(): void {
    if (this.topService.loading) return;
    this.router.navigate(['buyer/home']);
  }

  editOrder(): void {
    if (this.topService.loading) {
      return;
    }

    this.isEditingFromFullOrder.set(true);
    this.topService.loading = true;

    const selectedCustCode = this.userService.bpCode;
    if (selectedCustCode !== this.selectedOrder().cardCode) {
      this.ordersService.selected_doc_number = this.selectedOrder().docNumber;
      this.ordersService.selected_order_number = this.selectedOrder().docEntry;
      this.ordersService.handleFirstOrderLoadRequired = true;
      this.sessionService.selectedOrderNumber = this.ordersService.selected_order_number.toString();
      this.sessionService.selectedDocNumber = this.ordersService.selected_doc_number.toString();

      this.userService.sendSelectedCustomerToAPI(this.selectedOrder().cardCode).subscribe(
        () => {
          this.sessionService.isCustomerSelected = 'YES';
          this.sessionService.selectedCustomer = selectedCustCode;
          this.topService.isCustomerSelected = true;
          this.userService.getProfile().subscribe(
            (profile) => {
              this.userService.currentCustomer = `${profile.bpCode} - ${profile.companyName}`;
              if (profile.discount !== null) {
                this.userService.discount = profile.discount;
              } else {
                this.userService.discount = 0;
              }
              this.topService.hasOpenOrders = profile.hasOpenOrders;
              this.userService.companyName = profile.companyName;
              this.userService.bpCode = profile.bpCode;
              this.userService.billing = profile.billingAddresses;
              if (profile.billingAddresses.length > 0) {
                this.userService.billingDefault = profile.billingAddresses.find(a => a.isDefault);
                if (!this.userService.billingDefault) {
                  this.userService.billingDefault = profile.billingAddresses[0];
                }
              } else {
                this.userService.resetBilling();
              }
              if (profile.shippingAddresses.length > 0) {
                this.userService.shippingDefault = profile.shippingAddresses.find(a => a.isDefault);
                if (!this.userService.billingDefault) {
                  this.userService.billingDefault = profile.billingAddresses[0];
                }
              } else {
                this.userService.resetShipping();
              }
              this.userService.shipping = profile.shippingAddresses;
              this.userService.terms = profile.terms;
              this.userService.orderBankInfo = profile.beneficiaryBank;
              this.updateOrder();
            },
            (err) => {
              this.topService.loading = false;
              this.sharedService.handleBuyerHttpError(err, this.errorModal());
            });
        },
        (err) => {
          this.topService.loading = false;
          this.sharedService.handleBuyerHttpError(err, this.errorModal());
        });
    } else {
      this.closeModal();
      this.updateOrder();
    }

  }

  updateOrder(): void {
    this.ordersService.updateOrder(this.selectedOrder().docEntry).subscribe(
      () => {
        this.router.navigate(['cart'], { queryParams: { updatingOrder: 'Y' } });
      }, err => {
        this.topService.loading = false;
        this.sharedService.handleBuyerHttpError(err, this.errorModal());
      }
    );
  }

  showError(errorModal: models.IBuyerErrorModal): void {
    this.errorModal.set(errorModal);
  }

  hideError(): void {
    this.errorModal.update(error => ({ ...error, isShown: false }));
  }

  ngOnDestroy() {
    this.topService.trendingNavVisible = false;
    this.ordersService.selectedOrder = null;
  }

  splitOrder(): void {
    this.topService.loading = true;
    this.ordersService.getOrderDetails(this.selectedOrder().docEntry.toString()).subscribe(
      (orderData: models.IOrder) => {
        this.topService.loading = false;
        this.ordersService.orderChosen = orderData;
        this.router.navigate(['/user/split', this.selectedOrder().docEntry]);
      },
      (err) => {
        this.topService.loading = false;
        this.sharedService.handleBuyerHttpError(err, this.ordersService.ordersErrorModal);
      }
    );
  }

  showChargeModal() {
    this.showChargeOrder.set(true);
  }

  closeChargedModal() {
    this.showChargedOrder.set(false);
  }

  chargeOrder(): void {
    if (this.isSuper) {
      this.topService.loading = true;
      this.ordersService.chargeOrder(this.selectedOrder().payment.id, this.selectedOrder().orderTotal).subscribe(
        (res) => {
          this.topService.loading = false;
          this.showChargeOrder.set(false);
          if (res.error) {
            const modalData = {
              bodyText: res.error.message,
              isShown: true,
            };
            this.showError(modalData);
            return;
          }
          this.showChargedOrder.set(true);
          this.selectedOrder().payment.status = 'Applied';
        });
    }
  }

}
